// Single source of truth for app-wide knobs. Change behavior here, not in features.

const env = import.meta.env;
const flag = (v: string | undefined, fallback: boolean) =>
  v === undefined ? fallback : v.toLowerCase() !== 'false';

export interface AppConfig {
  apiBaseUrl: string;
  brand: { name: string; tagline: string };
  storage: { tokenKey: string };
  network: { requestTimeoutMs: number };
  auth: {
    providers: { google: boolean; facebook: boolean; x: boolean; email: boolean };
    emailVerify: boolean;
    googleClientId: string;
  };
  telegram: { expandOnLoad: boolean; closingConfirmation: boolean };
  catalog: { staleTimeMs: number };
  orders: { activePollIntervalMs: number; pageSize: number };
  query: { retry: number };
  iconPriority: boolean; // true = local first, false = server URL first
  // Auto-refresh cadence per screen; polling runs only while that screen is the
  // active (mounted, foregrounded) view.
  refresh: { homeMs: number; referralsMs: number };
  routes: {
    login: string;
    home: string;
    services: string;
    buy: string;
    orders: string;
    orderDetail: string;
    walletActivity: string;
    referrals: string;
    news: string;
    settings: string;
    verifyEmail: string;
    verifyEmailBase: string;
    resetPassword: string;
    resetPasswordBase: string;
  };
}

export const config: AppConfig = {
  apiBaseUrl: env.VITE_API_BASE_URL ?? 'http://localhost:5000',
  brand: { name: 'Boostly', tagline: 'Social Boost Tool' },
  storage: { tokenKey: 'boostly.jwt' },
  network: { requestTimeoutMs: 15_000 },
  auth: {
    providers: {
      // Social OAuth is not wired in the client yet (LoginScreen.onProvider just alerts),
      // so these default OFF to avoid showing dead buttons. Re-enable per provider via
      // VITE_AUTH_GOOGLE/FACEBOOK/X=true once the real OAuth flow is implemented.
      google: flag(env.VITE_AUTH_GOOGLE, false),
      facebook: flag(env.VITE_AUTH_FACEBOOK, false),
      x: flag(env.VITE_AUTH_X, false),
      email: flag(env.VITE_AUTH_EMAIL, true),
    },
    emailVerify: flag(env.VITE_AUTH_EMAIL_VERIFY, false),
    googleClientId: env.VITE_GOOGLE_CLIENT_ID ?? '',
  },
  telegram: { expandOnLoad: true, closingConfirmation: false },
  catalog: { staleTimeMs: 5 * 60_000 },
  orders: { activePollIntervalMs: 15_000, pageSize: 20 },
  query: { retry: 1 },
  iconPriority: flag(env.VITE_ICON_PRIORITY, false),
  refresh: { homeMs: 15_000, referralsMs: 20_000 },
  routes: {
    login: '/login',
    home: '/',
    services: '/services',
    buy: '/services/buy/:serviceId',
    orders: '/orders',
    orderDetail: '/orders/:orderId',
    walletActivity: '/wallet/activity',
    referrals: '/referrals',
    news: '/news',
    settings: '/settings',
    verifyEmail: '/verify-email/:token',
    verifyEmailBase: '/verify-email',
    resetPassword: '/reset-password/:token',
    resetPasswordBase: '/reset-password',
  },
};

export const buildRoute = {
  buy: (serviceId: number) => `/services/buy/${serviceId}`,
  orderDetail: (orderId: number | string) => `/orders/${orderId}`,
};
